/**
 * @author      patrick@zumper.com (Patrick Giddings)
 * @copyright   Copyright (c) 2023, Zumper
 */

import { combineReducers, createSlice } from '@reduxjs/toolkit'
import { createTypedPayloadReducer } from '@zcode-ui/web-zumper-features'
import type { RentResearchDetailData } from '@zcode/state-type'

import { RENT_RESEARCH_DETAIL_INITIAL_STATE, RENT_RESEARCH_DETAIL_SLICE_NAME } from '../constants'

const costOfLivingCategoriesSlice = createSlice({
  name: `${RENT_RESEARCH_DETAIL_SLICE_NAME}/data/costOfLivingCategories`,
  initialState: RENT_RESEARCH_DETAIL_INITIAL_STATE.data.costOfLivingCategories,
  reducers: {
    receiveRentResearchDetailCostOfLivingCategories:
      createTypedPayloadReducer<RentResearchDetailData['costOfLivingCategories']>(),
  },
})

const discoverSlice = createSlice({
  name: `${RENT_RESEARCH_DETAIL_SLICE_NAME}/data/discover`,
  initialState: RENT_RESEARCH_DETAIL_INITIAL_STATE.data.discover,
  reducers: {
    receiveRentResearchDetailDiscover:
      createTypedPayloadReducer<RentResearchDetailData['discover']>(),
  },
})

const linkpacksSlice = createSlice({
  name: `${RENT_RESEARCH_DETAIL_SLICE_NAME}/data/linkpacks`,
  initialState: RENT_RESEARCH_DETAIL_INITIAL_STATE.data.linkpacks,
  reducers: {
    receiveRentResearchDetailLinkpacks:
      createTypedPayloadReducer<RentResearchDetailData['linkpacks']>(),
  },
})

const locationDataSlice = createSlice({
  name: `${RENT_RESEARCH_DETAIL_SLICE_NAME}/data/locationData`,
  initialState: RENT_RESEARCH_DETAIL_INITIAL_STATE.data.locationData,
  reducers: {
    receiveRentResearchDetailLocationData:
      createTypedPayloadReducer<RentResearchDetailData['locationData']>(),
  },
})

const rentalDataSlice = createSlice({
  name: `${RENT_RESEARCH_DETAIL_SLICE_NAME}/data/rentalData`,
  initialState: RENT_RESEARCH_DETAIL_INITIAL_STATE.data.rentalData,
  reducers: {
    receiveRentResearchDetailRentalData:
      createTypedPayloadReducer<RentResearchDetailData['rentalData']>(),
  },
})

const renterOccupiedPercentageSlice = createSlice({
  name: `${RENT_RESEARCH_DETAIL_SLICE_NAME}/data/renterOccupiedPercentage`,
  initialState: RENT_RESEARCH_DETAIL_INITIAL_STATE.data.renterOccupiedPercentage,
  reducers: {
    receiveRentResearchDetailRenterOccupiedPercentage:
      createTypedPayloadReducer<RentResearchDetailData['renterOccupiedPercentage']>(),
  },
})

export const rentResearchDetailDataSlice = {
  name: `${RENT_RESEARCH_DETAIL_SLICE_NAME}/data`,
  reducer: combineReducers({
    costOfLivingCategories: costOfLivingCategoriesSlice.reducer,
    discover: discoverSlice.reducer,
    linkpacks: linkpacksSlice.reducer,
    locationData: locationDataSlice.reducer,
    rentalData: rentalDataSlice.reducer,
    renterOccupiedPercentage: renterOccupiedPercentageSlice.reducer,
  }),
  actions: {
    ...costOfLivingCategoriesSlice.actions,
    ...discoverSlice.actions,
    ...linkpacksSlice.actions,
    ...locationDataSlice.actions,
    ...rentalDataSlice.actions,
    ...renterOccupiedPercentageSlice.actions,
  },
} as const
